import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandLoading,
} from "@hortis/ui/command";
import { toast } from "@hortis/ui/sonner";
import { A, F, pipe } from "@mobily/ts-belt";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { namedOperations, useGetLocationsQuery } from "generated/graphql";
import { updatePlantMaterialsDocument } from "src/features/actions-menu/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";

interface CommandMenuLocationPageProps {
  selectedMaterials: ReadonlyArray<{ id: string; locationId?: string }>;
  onSuccess: () => void;
}

const CommandMenuLocationPage = ({
  selectedMaterials,
  onSuccess,
}: CommandMenuLocationPageProps) => {
  const queryClient = useQueryClient();
  const { subdomain } = useRouteContext({ from: "/_layout/site/$siteSlug" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });
  const updatePlantMaterials = useGqlMutation({
    mutation: updatePlantMaterialsDocument,
    onError: () => {
      toast.error("Failed to update materials");
    },
    onSuccess: (res) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Tests flake seeming to return nullable removeTagPlantMaterials
      if (!res.updatePlantMaterials?.success) {
        throw new Error("Failed to update materials");
      }
      toast.success(
        `Successfully updated ${
          res.updatePlantMaterials.plantMaterials.length
        } material${
          res.updatePlantMaterials.plantMaterials.length > 1 ? "s" : ""
        }`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      onSuccess();
    },
  });
  const [{ data, fetching }] = useGetLocationsQuery({
    variables: {
      siteSlug,
      subdomain,
    },
  });
  const locations = data?.org?.site?.locationsConnection?.nodes;

  const handleLocationSelect = (locationId: string) => {
    const ids = pipe(
      selectedMaterials,
      A.filter((m) => m.locationId !== locationId),
      A.map(({ id }) => id),
      F.toMutable,
    );
    if (ids.length === 0) {
      onSuccess();
      return;
    }
    updatePlantMaterials.mutate({
      ids,
      input: {
        locationId,
      },
    });
  };

  return (
    <>
      {fetching && locations == null && <CommandLoading />}
      <CommandGroup data-cy="command-menu-location-page">
        {locations?.map((location) => (
          <CommandItem
            key={location.id}
            onSelect={() => {
              handleLocationSelect(location.id);
            }}
            loading={
              updatePlantMaterials.variables?.input.locationId ===
                location.id && updatePlantMaterials.isPending
            }
            disabled={updatePlantMaterials.isPending}
            data-cy={`${location.name}-location-menu-item`}
          >
            {location.name}
            <span className="font-normal text-grey-500">
              {location.description}
            </span>
          </CommandItem>
        ))}
      </CommandGroup>
      <CommandEmpty>No results</CommandEmpty>
    </>
  );
};

export { CommandMenuLocationPage };
